/**
 * Book page — /book route.
 *
 * States:
 * 1. loading: fetching initial data
 * 2. empty: no chapters — BookSettings panel + Generate Book button
 * 3. generating: outline job in-progress — GenerationProgress component
 * 4. outline-review: outline ready, awaiting approval — OutlineReview component
 * 5. ready: chapters exist — BookStudio three-pane writing app
 * 6. error: identity/load failed
 */
import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getUserIdentity } from '../lib/auth';
import { fetchWithAuth } from '../lib/api';
import { BookStudio } from '../book/BookStudio';
import { BookPreview } from '../book/BookPreview';
import { MobileOverflowSheet } from '../book/MobileOverflowSheet';
import { GenerationProgress } from '../book/GenerationProgress';
import type { GenerationSummary } from '../book/GenerationProgress';
import { SharePanel } from '../book/SharePanel';
import { BookSettings, loadBookSettings, DEFAULT_BOOK_CONFIG } from '../book/BookSettings';
import { fetchProfile } from '../lib/profile-api';
import { OutlineReview } from '../book/OutlineReview';
import { VersionHistoryPanel } from '../book/VersionHistoryPanel';
import { ChapterAssignment } from '../book/ChapterAssignment';
import { OrderModal } from '../book/OrderModal';
import { usePdfExport } from '../hooks/usePdfExport';
import { fetchLatestPdf, pollPdfStatus, triggerPdfExport } from '../lib/orders-api';
import type { BookConfig } from '../book/BookSettings';

type BookState = 'loading' | 'empty' | 'chapter-assignment' | 'generating' | 'outline-review' | 'ready' | 'error';
type PrintStep = 'write' | 'prepare' | 'print';

interface BookSectionSummary {
  id: string;
  type: string;
  title: string;
  body: string;
  locked: boolean;
  include: boolean;
  version: number;
}
interface BookDocumentResponse {
  sections: BookSectionSummary[];
  version: number;
}

export function Book() {
  const [pageState, setPageState] = useState<BookState>('loading');
  const [sub, setSub] = useState<string>('');
  const [bookHtml, setBookHtml] = useState<string | null>(null);
  const [bookDoc, setBookDoc] = useState<BookDocumentResponse | null>(null);
  const [currentJobId, setCurrentJobId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [bookConfig, setBookConfig] = useState<BookConfig>({ ...DEFAULT_BOOK_CONFIG });
  const [authorName, setAuthorName] = useState<string>('');
  const [titleSuggestions, setTitleSuggestions] = useState<string[]>([]);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [overflowOpen, setOverflowOpen] = useState(false);
  const [orderModalOpen, setOrderModalOpen] = useState(false);
  const [activeStep, setActiveStep] = useState<PrintStep>('write');
  const [prepareLoading, setPrepareLoading] = useState(false);
  const [prepareStage, setPrepareStage] = useState<string | null>(null);
  const [prepareError, setPrepareError] = useState<string | null>(null);
  const [generationSummary, setGenerationSummary] = useState<string | null>(null);
  const [preparedInteriorUrl, setPreparedInteriorUrl] = useState<string | null>(null);
  const [preparedCoverUrl, setPreparedCoverUrl] = useState<string | null>(null);
  const [preparedPageCount, setPreparedPageCount] = useState<number | null>(null);
  const { loading: pdfLoading, error: pdfError, copied: pdfCopied, handleDownload: handleDownloadPdf, handleShare: handleSharePdf } = usePdfExport();
  const navigate = useNavigate();

  const REST_URL = import.meta.env.VITE_REST_API_URL as string;
  const showSectionLockToastRef = useRef<((sectionId: string, message: string) => void) | null>(null);

  const saveBookConfig = useCallback(async (config: BookConfig) => {
    setBookConfig(config);
    await fetchWithAuth(`${REST_URL}book/settings`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(config),
    }).catch(() => {});
  }, [REST_URL]);

  const triggerSectionGenerate = useCallback(async (sectionId: string) => {
    try {
      const res = await fetchWithAuth(`${REST_URL}book/section/${sectionId}/generate`, { method: 'POST' });
      if (res.ok) {
        const data = await res.json() as { sectionId: string; body: string; version: number };
        // Reload the book to pick up the generated body
        if (data.body) {
          const reloadRes = await fetchWithAuth(`${REST_URL}book`);
          if (reloadRes.ok) {
            const doc = await reloadRes.json() as BookDocumentResponse;
            const html = buildBookHtml(doc);
            setBookHtml(html);
          }
        }
      }
    } catch {
      // Non-blocking — user can retry manually if generation fails
    }
  }, [REST_URL]);

  const buildBookHtml = (doc: BookDocumentResponse): string => {
    return doc.sections
      .filter(s => s.include)
      .map(s => {
        if (s.type === 'chapter') {
          return `<section data-section-id="${s.id}" data-section-type="${s.type}">${s.body}</section>`;
        }
        if (s.type === 'title-page') {
          return `<section class="title-page" data-section-id="${s.id}" data-section-type="title-page">${s.body}</section>`;
        }
        if (s.type === 'dedication') {
          const body = s.body || '<p class="dedication-text"></p>';
          return `<section class="dedication-page" data-section-id="${s.id}" data-section-type="dedication">${body}</section>`;
        }
        if (s.type === 'back-matter') {
          const body = s.body || '<div class="back-matter-content"></div>';
          return `<section class="back-matter" data-section-id="${s.id}" data-section-type="back-matter">${body}</section>`;
        }
        if (s.type === 'toc') {
          const body = s.body || '';
          return `<section class="toc-page" data-section-id="${s.id}" data-section-type="toc">${body}</section>`;
        }
        if (s.type === 'index') {
          const body = s.body || '';
          return `<section class="index-page" data-section-id="${s.id}" data-section-type="index">${body}</section>`;
        }
        return s.body;
      })
      .join('\n');
  };

  const sectionLocks = useMemo(() => {
    const locks: Record<string, boolean> = {};
    for (const s of bookDoc?.sections ?? []) {
      if (s.locked) locks[s.id] = true;
    }
    return locks;
  }, [bookDoc]);

  const readiness = useCallback(() => {
    const sections = bookDoc?.sections ?? [];
    const included = sections.filter(s => s.include);
    return {
      chapterCount: included.filter(s => s.type === 'chapter').length,
      lockedCount: included.filter(s => s.locked).length,
      emptyCount: included.filter(s => s.body.trim().length === 0).length,
      missingMatterCount: ['title-page', 'dedication', 'toc', 'index', 'back-matter']
        .filter(type => !included.some(s => s.type === type)).length,
    };
  }, [bookDoc]);

  const loadChapters = useCallback(async () => {
    try {
      const res = await fetchWithAuth(`${REST_URL}chapters`);
      const data = await res.json() as { chapters: { id: string }[] };
      if (data.chapters.length > 0) {
        // Try structured GET /book first
        const structuredRes = await fetchWithAuth(`${REST_URL}book`);
        if (structuredRes.ok) {
          const doc = await structuredRes.json() as BookDocumentResponse;
          const html = buildBookHtml(doc);
          setBookDoc(doc);
          setBookHtml(html);
          setPageState('ready');
          fetchLatestPdf().then(result => {
            if (result.exists) {
              setPreparedInteriorUrl(result.downloadUrl);
              setPreparedPageCount(result.pageCount ?? null);
            }
          }).catch(() => {});
          fetchWithAuth(`${REST_URL}cover/current`).then(async coverRes => {
            if (coverRes.ok) {
              const cover = await coverRes.json() as { coverPdfUrl?: string };
              setPreparedCoverUrl(cover.coverPdfUrl ?? null);
            }
          }).catch(() => {});
          // Lazy-trigger AI generation for empty front/back matter sections
          for (const section of doc.sections) {
            if ((section.type === 'dedication' || section.type === 'back-matter') && !section.body.trim()) {
              void triggerSectionGenerate(section.id);
            }
          }
          return;
        }
        // Fallback: GET /book/html (pre-migration users)
        const bookRes = await fetchWithAuth(`${REST_URL}book/html`);
        if (bookRes.ok) {
          setBookHtml(await bookRes.text());
          setPageState('ready');
        } else {
          setPageState('empty');
        }
      } else {
        setPageState('empty');
      }
    } catch {
      setPageState('empty');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [REST_URL, triggerSectionGenerate]);

  useEffect(() => {
    getUserIdentity().then(identity => {
      setSub(identity.sub);
      Promise.all([
        loadBookSettings(REST_URL).then(setBookConfig),
        loadChapters(),
        fetchProfile().then(profile => {
          if (profile?.fullName) setAuthorName(profile.fullName);
        }).catch(() => {}),
      ]).catch(() => {});
    }).catch(() => setPageState('error'));
  }, [loadChapters, REST_URL]);

  const generateBook = async () => {
    setError(null);
    setPageState('chapter-assignment');
  };

  const pollCoverStatus = useCallback((jobId: string): Promise<string | null> => {
    return new Promise(resolve => {
      let attempts = 0;
      const poll = () => {
        if (attempts > 60) {
          resolve(null);
          return;
        }
        attempts += 1;
        fetchWithAuth(`${REST_URL}cover/status/${encodeURIComponent(jobId)}`)
          .then(async res => {
            if (!res.ok) throw new Error('cover status failed');
            const status = await res.json() as { status: string; downloadUrl?: string };
            if (status.status === 'complete') {
              resolve(status.downloadUrl ?? null);
            } else if (status.status === 'error') {
              resolve(null);
            } else {
              setTimeout(poll, 3000);
            }
          })
          .catch(() => setTimeout(poll, 3000));
      };
      poll();
    });
  }, [REST_URL]);

  const preparePrintFiles = useCallback(async () => {
    if (prepareLoading) return;
    setPrepareLoading(true);
    setPrepareStage('Rendering interior PDF...');
    setError(null);
    setPrepareError(null);
    try {
      const { jobId } = await triggerPdfExport();
      let interiorUrl: string | null = null;
      let pageCount = 200;
      const POLL_BUDGET_MS = 6 * 60 * 1000;
      const startedAt = Date.now();
      let delay = 3000;
      while (Date.now() - startedAt < POLL_BUDGET_MS) {
        await new Promise(r => setTimeout(r, delay));
        delay = Math.min(delay + 1000, 10000);
        const status = await pollPdfStatus(jobId);
        if (status.status === 'complete' && status.downloadUrl) {
          interiorUrl = status.downloadUrl;
          pageCount = status.pageCount ?? pageCount;
          break;
        }
        if (status.status === 'error' || status.status === 'failed') {
          throw new Error('PDF generation failed');
        }
      }
      if (!interiorUrl) throw new Error('PDF generation timed out');

      setPreparedInteriorUrl(interiorUrl);
      setPreparedPageCount(pageCount);
      setPrepareStage('Building full cover PDF...');

      const coverRes = await fetchWithAuth(`${REST_URL}cover/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          pageCount,
          bookTitle: bookConfig.title || 'My Memoir',
          authorName,
        }),
      });
      if (!coverRes.ok) throw new Error('Cover generation failed');
      const { jobId: coverJobId } = await coverRes.json() as { jobId: string };
      const coverUrl = await pollCoverStatus(coverJobId);
      if (!coverUrl) throw new Error('Cover generation failed');
      setPreparedCoverUrl(coverUrl);
      setActiveStep('print');
    } catch (err) {
      setPrepareError(
        err instanceof Error && err.message === 'PDF generation timed out'
          ? 'Preparing print files took longer than expected. The job may still be running — retry to check again.'
          : 'Could not prepare Lulu print files.',
      );
    } finally {
      setPrepareLoading(false);
      setPrepareStage(null);
    }
  }, [REST_URL, authorName, bookConfig.title, pollCoverStatus, prepareLoading]);

  const handleChapterAssignmentApprove = useCallback(async (_chapterAssignment: Record<string, string[]>) => {
    setError(null);
    try {
      const res = await fetchWithAuth(`${REST_URL}book/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ bookConfig }),
      });
      if (res.status === 409) {
        const data = await res.json() as { error: string; sectionId: string; title: string };
        showSectionLockToastRef.current?.(data.sectionId, 'Chapter locked — unlock to regenerate');
        setPageState(pageState === 'ready' ? 'ready' : 'empty');
        return;
      }
      if (!res.ok) throw new Error('Generation request failed');
      const data = await res.json() as { jobId: string };
      setCurrentJobId(data.jobId);
      setPageState('generating');
    } catch {
      setError('Could not start generation. Try again.');
      setPageState(pageState === 'ready' ? 'ready' : 'empty');
    }
  }, [REST_URL, bookConfig, pageState]);

  const handleRegenerateChapter = useCallback(async (sectionId: string, instruction: string) => {
    setError(null);
    try {
      const res = await fetchWithAuth(`${REST_URL}book/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sectionIds: [sectionId],
          ...(instruction ? { instruction } : {}),
        }),
      });
      if (res.status === 409) {
        showSectionLockToastRef.current?.(sectionId, 'Chapter locked — unlock to regenerate');
        return;
      }
      if (!res.ok) throw new Error('Regeneration request failed');
      const data = await res.json() as { jobId: string };
      setCurrentJobId(data.jobId);
      setPageState('generating');
    } catch {
      // Surface inline next to the chapter row — the studio is still on screen here
      showSectionLockToastRef.current?.(sectionId, 'Could not start regeneration — try again');
    }
  }, [REST_URL]);

  const handleGenerationComplete = useCallback((summary?: GenerationSummary) => {
    setCurrentJobId(null);
    if (typeof summary?.regeneratedCount === 'number') {
      const parts = [`Regenerated ${summary.regeneratedCount} chapter${summary.regeneratedCount === 1 ? '' : 's'}`];
      if (summary.preservedLockedCount) {
        parts.push(`${summary.preservedLockedCount} locked chapter${summary.preservedLockedCount === 1 ? '' : 's'} preserved`);
      }
      setGenerationSummary(parts.join(' · '));
    }
    setPageState('loading');
    loadChapters().catch(() => setPageState('error'));
  }, [loadChapters]);

  useEffect(() => {
    if (!generationSummary) return;
    const timer = setTimeout(() => setGenerationSummary(null), 12000);
    return () => clearTimeout(timer);
  }, [generationSummary]);

  const handleGenerationError = useCallback((msg: string) => {
    setError(msg);
    setCurrentJobId(null);
    setPageState('empty');
  }, []);

  // handleOutlineReady removed — Phase 68 eliminates the outline review gate

  const handleBookSave = useCallback((newHtml: string) => {
    setBookHtml(newHtml);
  }, []);

  return (
    <main
      className={
        pageState === 'ready' || pageState === 'chapter-assignment'
          ? 'h-dvh flex flex-col bg-stone-50 overflow-hidden relative'
          : 'min-h-dvh bg-stone-50 flex flex-col items-center py-12 px-4'
      }
    >
      {/* Ready state: slim header bar + full-height BookStudio */}
      {pageState === 'ready' && (
        <>
          {/* Mobile header — title + Download PDF + overflow trigger */}
          <div className="bg-white border-b border-stone-200 px-4 py-2 flex items-center gap-2 h-12 flex-shrink-0 z-10 md:hidden">
            <h1 className="text-base font-serif text-stone-900 mr-auto">My Memoir</h1>
            <button
              type="button"
              onClick={handleDownloadPdf}
              disabled={pdfLoading}
              className="px-3 py-1.5 rounded-lg bg-amber-500 text-white text-sm font-semibold hover:bg-amber-600 transition-colors disabled:opacity-50"
            >
              {pdfLoading ? 'Preparing PDF…' : 'Download PDF'}
            </button>
            <button
              type="button"
              onClick={() => setOverflowOpen(true)}
              aria-label="More options"
              className="flex items-center justify-center w-11 h-11 text-stone-600 hover:text-stone-800 text-xl leading-none"
            >
              ···
            </button>
          </div>

          {/* Desktop header — all buttons, unchanged layout */}
          <div className="bg-white border-b border-stone-200 px-6 py-2 hidden md:flex items-center gap-3 flex-shrink-0 z-10">
            <h1 className="text-xl font-serif text-stone-900 mr-2">My Memoir</h1>
            {(['write', 'prepare', 'print'] as const).map(step => (
              <button
                key={step}
                type="button"
                onClick={() => setActiveStep(step)}
                className={`px-3 py-1.5 rounded-lg text-sm font-semibold transition-colors ${
                  activeStep === step
                    ? 'bg-stone-900 text-white'
                    : 'border border-stone-300 text-stone-700 hover:bg-stone-100'
                }`}
              >
                {step[0].toUpperCase() + step.slice(1)}
              </button>
            ))}

            <button
              type="button"
              onClick={generateBook}
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-stone-300 text-stone-700 text-sm font-semibold hover:bg-stone-100 transition-colors"
            >
              Generate Chapters
            </button>

            <button
              type="button"
              onClick={handleDownloadPdf}
              disabled={pdfLoading}
              className="flex items-center gap-2 px-3 py-1.5 rounded-xl bg-amber-500 text-white text-sm font-semibold hover:bg-amber-600 transition-colors disabled:opacity-50"
            >
              {pdfLoading ? 'Preparing PDF…' : 'Download Interior'}
            </button>

            <button
              type="button"
              onClick={handleSharePdf}
              disabled={pdfLoading}
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-stone-300 text-stone-700 text-sm font-semibold hover:bg-stone-100 transition-colors disabled:opacity-50"
            >
              {pdfCopied ? 'Link copied!' : (pdfLoading ? 'Preparing…' : 'Share PDF')}
            </button>

            {pdfError && (
              <span className="text-red-700 bg-red-50 border border-red-200 rounded p-2 text-sm">
                {pdfError}
              </span>
            )}

            <button
              type="button"
              onClick={() => setPreviewOpen(true)}
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-stone-300 text-stone-700 text-sm font-semibold hover:bg-stone-100 transition-colors"
            >
              Preview
            </button>

            <button
              type="button"
              onClick={() => setOrderModalOpen(true)}
              className="flex items-center gap-2 px-3 py-1.5 rounded-xl bg-amber-500 text-white text-sm font-semibold hover:bg-amber-600 transition-colors"
            >
              Submit to Lulu
            </button>

            <button
              type="button"
              onClick={() => navigate('/orders')}
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-stone-300 text-stone-700 text-sm font-semibold hover:bg-stone-100 transition-colors"
            >
              My printed books
            </button>

            <button
              type="button"
              onClick={() => { setSettingsOpen(o => !o); setHistoryOpen(false); }}
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-stone-300 text-stone-700 text-sm font-semibold hover:bg-stone-100 transition-colors"
            >
              {settingsOpen ? 'Hide Settings' : 'Settings'}
            </button>

            <button
              type="button"
              onClick={() => { setHistoryOpen(o => !o); setSettingsOpen(false); }}
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-stone-300 text-stone-700 text-sm font-semibold hover:bg-stone-100 transition-colors"
            >
              {historyOpen ? 'Hide History' : 'History'}
            </button>

            <button
              type="button"
              onClick={() => navigate('/')}
              className="ml-auto text-stone-400 hover:text-stone-600 text-sm"
            >
              Back to Dashboard
            </button>
          </div>

          {/* Book Settings Panel — floating overlay */}
          {settingsOpen && (
            <div className="absolute top-14 left-4 right-4 z-30 max-w-2xl mx-auto">
              <BookSettings
                config={bookConfig}
                onChange={saveBookConfig}
                titleSuggestions={titleSuggestions}
              />
            </div>
          )}

          {/* Version History Panel — floating overlay */}
          {historyOpen && (
            <div className="absolute top-14 left-4 right-4 z-30 max-w-2xl mx-auto">
              <VersionHistoryPanel
                restUrl={REST_URL}
                onRestoreComplete={() => {
                  setPageState('loading');
                  loadChapters().catch(() => setPageState('error'));
                }}
              />
            </div>
          )}

          {/* BookStudio — fills remaining viewport height */}
          {activeStep === 'prepare' && (
            <div className="border-b border-stone-200 bg-white px-6 py-4">
              <div className="max-w-5xl mx-auto grid gap-4 md:grid-cols-[1fr_auto] md:items-center">
                <div>
                  <h2 className="font-semibold text-stone-900">Book Readiness</h2>
                  <p className="text-sm text-stone-500 mt-1">
                    Prepare creates the Lulu-ready interior PDF and full wrap cover PDF. Missing publishing sections are generated automatically.
                  </p>
                  {(() => {
                    const r = readiness();
                    return (
                      <div className="mt-3 flex flex-wrap gap-2 text-xs text-stone-600">
                        <span className="rounded-full bg-stone-100 px-3 py-1">{r.chapterCount} chapters included</span>
                        <span className="rounded-full bg-stone-100 px-3 py-1">{r.lockedCount} locked included as written</span>
                        <span className="rounded-full bg-stone-100 px-3 py-1">{r.emptyCount} empty will be generated/refreshed</span>
                        <span className="rounded-full bg-stone-100 px-3 py-1">{r.missingMatterCount} missing publishing sections added</span>
                      </div>
                    );
                  })()}
                </div>
                <button
                  type="button"
                  onClick={preparePrintFiles}
                  disabled={prepareLoading}
                  className="px-5 py-2.5 rounded-xl bg-amber-500 text-white text-sm font-semibold hover:bg-amber-600 transition-colors disabled:opacity-50"
                >
                  {prepareLoading ? (prepareStage ?? 'Preparing...') : preparedInteriorUrl && preparedCoverUrl ? 'Refresh Print Files' : 'Prepare Print Files'}
                </button>
              </div>
              {prepareError && (
                <div className="max-w-5xl mx-auto mt-3 flex items-center justify-between gap-3 text-red-700 bg-red-50 border border-red-200 rounded p-3 text-sm">
                  <span>{prepareError}</span>
                  <button
                    type="button"
                    onClick={preparePrintFiles}
                    className="flex-none font-semibold underline hover:text-red-800 transition-colors"
                  >
                    Retry
                  </button>
                </div>
              )}
            </div>
          )}

          {activeStep === 'print' && (
            <div className="border-b border-stone-200 bg-white px-6 py-4">
              <div className="max-w-5xl mx-auto flex flex-wrap items-center gap-3">
                <div className="mr-auto">
                  <h2 className="font-semibold text-stone-900">Print Files</h2>
                  <p className="text-sm text-stone-500">
                    {preparedPageCount ? `${preparedPageCount} interior pages prepared for 6 x 9 paperback.` : 'Prepare print files before downloading or submitting.'}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => preparedInteriorUrl && window.open(preparedInteriorUrl, '_blank')}
                  disabled={!preparedInteriorUrl}
                  className="px-4 py-2 rounded-lg border border-stone-300 text-stone-700 text-sm font-semibold hover:bg-stone-100 disabled:opacity-50"
                >
                  Download Interior PDF
                </button>
                <button
                  type="button"
                  onClick={() => preparedCoverUrl && window.open(preparedCoverUrl, '_blank')}
                  disabled={!preparedCoverUrl}
                  className="px-4 py-2 rounded-lg border border-stone-300 text-stone-700 text-sm font-semibold hover:bg-stone-100 disabled:opacity-50"
                >
                  Download Cover PDF
                </button>
                <button
                  type="button"
                  onClick={() => setOrderModalOpen(true)}
                  disabled={!preparedInteriorUrl || !preparedCoverUrl}
                  className="px-4 py-2 rounded-xl bg-amber-500 text-white text-sm font-semibold hover:bg-amber-600 disabled:opacity-50"
                >
                  Submit to Lulu
                </button>
              </div>
            </div>
          )}

          {bookHtml && activeStep === 'write' && (
            <div className="flex-1 overflow-hidden">
              <BookStudio
                bookHtml={bookHtml}
                restUrl={REST_URL}
                onSave={handleBookSave}
                onRefreshBook={() => { setPageState('loading'); loadChapters().catch(() => setPageState('error')); }}
                showSectionLockToastRef={showSectionLockToastRef}
                bookTitle={bookConfig.title}
                authorName={authorName}
                sectionLocks={sectionLocks}
                onRegenerateChapter={handleRegenerateChapter}
              />
            </div>
          )}

          {/* Generation summary toast */}
          {generationSummary && (
            <div
              role="status"
              className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-stone-800 text-white text-sm px-4 py-2 rounded-lg shadow-lg z-50 flex items-center gap-3"
            >
              <span>{generationSummary}</span>
              <button
                type="button"
                onClick={() => setGenerationSummary(null)}
                aria-label="Dismiss"
                className="text-stone-300 hover:text-white"
              >
                ✕
              </button>
            </div>
          )}

          {/* Mobile overflow sheet */}
          <MobileOverflowSheet
            open={overflowOpen}
            onClose={() => setOverflowOpen(false)}
            onRegenerate={() => { generateBook(); }}
            onPreview={() => { setPreviewOpen(true); }}
            onHistory={() => { setHistoryOpen(o => !o); setSettingsOpen(false); }}
            onDashboard={() => navigate('/')}
            onDownloadPdf={handleDownloadPdf}
            onSharePdf={handleSharePdf}
            onOrderBook={() => { setOrderModalOpen(true); }}
          />
        </>
      )}

      {/* Chapter-assignment state: full-pane pre-generation screen (Phase 31) */}
      {pageState === 'chapter-assignment' && (
        <ChapterAssignment
          restUrl={REST_URL}
          onApprove={handleChapterAssignmentApprove}
          onCancel={() => setPageState(bookHtml ? 'ready' : 'empty')}
        />
      )}

      {/* Non-ready states — centered layout */}
      {pageState !== 'ready' && pageState !== 'chapter-assignment' && (
        <div className="w-full max-w-2xl space-y-6">
          {/* Header */}
          <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-8">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h1 className="text-3xl font-serif text-stone-900">My Memoir</h1>
                <p className="text-stone-500 text-sm mt-1">Your autobiography in progress.</p>
              </div>
              <button
                type="button"
                onClick={() => navigate('/')}
                className="text-stone-400 hover:text-stone-600 text-sm"
              >
                Back to Dashboard
              </button>
            </div>

            {error && (
              <div className="text-red-700 bg-red-50 border border-red-200 rounded p-3 text-sm mb-4">{error}</div>
            )}

            {pageState === 'loading' && (
              <div className="text-stone-400 text-sm py-4 text-center">Loading your memoir...</div>
            )}

            {pageState === 'empty' && (
              <div className="space-y-4 text-center py-4">
                <p className="text-xl font-serif text-stone-900">Your autobiography is waiting.</p>
                <p className="text-stone-500 text-sm">Generate your book to transform your memories into a polished memoir chapter by chapter.</p>
                <button
                  type="button"
                  onClick={generateBook}
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-amber-500 text-white font-semibold hover:bg-amber-600 transition-colors focus:outline-none focus:ring-2 focus:ring-amber-400 focus:ring-offset-2"
                >
                  Generate Book
                </button>
              </div>
            )}

            {pageState === 'error' && (
              <div className="text-stone-500 text-sm py-4 text-center">
                Unable to load your memoir. Please try refreshing.
              </div>
            )}
          </div>

          {/* Book Settings — visible in empty state */}
          {pageState === 'empty' && (
            <BookSettings
              config={bookConfig}
              onChange={saveBookConfig}
              titleSuggestions={titleSuggestions}
            />
          )}

          {/* Generation progress */}
          {pageState === 'generating' && currentJobId && sub && (
            <GenerationProgress
              jobId={currentJobId}
              sub={sub}
              onComplete={handleGenerationComplete}
              onError={handleGenerationError}
            />
          )}

          {/* Outline review */}
          {pageState === 'outline-review' && currentJobId && (
            <OutlineReview
              jobId={currentJobId}
              restUrl={REST_URL}
              onApproved={() => setPageState('generating')}
              onCancelled={() => {
                setCurrentJobId(null);
                setPageState('empty');
              }}
              onTitleSuggestions={setTitleSuggestions}
            />
          )}

          {/* Share panel */}
          <SharePanel />
        </div>
      )}

      {/* BookPreview — fullscreen paginated preview overlay */}
      {previewOpen && bookHtml && (
        <BookPreview
          bookHtml={bookHtml}
          onClose={() => setPreviewOpen(false)}
        />
      )}

      {/* OrderModal — Stripe checkout wizard */}
      {orderModalOpen && bookHtml && (
        <OrderModal
          open={orderModalOpen}
          onClose={() => setOrderModalOpen(false)}
          bookTitle={bookConfig.title}
          bookHtml={bookHtml}
        />
      )}
    </main>
  );
}
